/**
 * SearchMultiplierSlider — shared UI for tuning the drilled-envelope
 * filter used in the Mineral Resource Statement (Session I-37 · Part 8).
 *
 * Composition:
 *   1. Header with title + explanation
 *   2. Range input (0..4, step 0.25) with an "Off" button
 *   3. Live stats grid (spacing, radius, collars, blocks kept)
 *   4. Mini "resource tonnage vs multiplier" preview chart (inline SVG)
 *
 * Used by:
 *   • Stage 8 Report screen (`Stage13ReportCompliance.js`)
 *   • Block Model tab classification screen (`BlockModelTab.js`)
 */
import { useEffect, useState, useMemo } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

function formatMt(t) {
  const mt = (t || 0) / 1_000_000;
  if (mt >= 1_000_000) return `${(mt / 1_000_000).toFixed(2)} Bt`;
  if (mt >= 1_000)     return `${(mt / 1_000).toFixed(2)} Gt`;
  return `${mt.toFixed(2)} Mt`;
}

function SparklineChart({ points, activeMult, onPick }) {
  const W = 320, H = 90, PAD_L = 34, PAD_R = 8, PAD_T = 8, PAD_B = 18;
  const iw = W - PAD_L - PAD_R;
  const ih = H - PAD_T - PAD_B;

  const { pathD, dots, ticks, elbow } = useMemo(() => {
    if (!points || points.length === 0) return { pathD: "", dots: [], ticks: [], elbow: null };
    const xs = points.map((p) => p.multiplier);
    const ys = points.map((p) => p.tonnage || 0);
    const xMin = Math.min(...xs), xMax = Math.max(...xs);
    const yMax = Math.max(...ys) * 1.05 || 1;
    const scaleX = (x) => PAD_L + ((x - xMin) / (xMax - xMin)) * iw;
    const scaleY = (y) => PAD_T + ih - (y / yMax) * ih;

    const d = points.map((p, i) => {
      const cmd = i === 0 ? "M" : "L";
      return `${cmd}${scaleX(p.multiplier).toFixed(1)},${scaleY(p.tonnage).toFixed(1)}`;
    }).join(" ");

    const dotList = points.map((p) => ({
      cx: scaleX(p.multiplier), cy: scaleY(p.tonnage), m: p.multiplier, t: p.tonnage,
    }));

    // Elbow detection: point with largest 2nd derivative (curvature).
    let elbowIdx = -1, bestCurv = 0;
    for (let i = 1; i < points.length - 1; i += 1) {
      const t0 = points[i - 1].tonnage, t1 = points[i].tonnage, t2 = points[i + 1].tonnage;
      const curv = Math.abs(t2 - 2 * t1 + t0);
      if (curv > bestCurv) { bestCurv = curv; elbowIdx = i; }
    }
    const elbowP = elbowIdx > 0 ? { ...dotList[elbowIdx], m: points[elbowIdx].multiplier } : null;

    const tickList = points.filter((_, i) => i % 2 === 0).map((p) => ({
      x: scaleX(p.multiplier), label: `${p.multiplier}×`,
    }));

    return { pathD: d, dots: dotList, ticks: tickList, elbow: elbowP };
  }, [points, iw, ih]);

  if (!points || points.length === 0) return null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" data-testid="search-mult-preview-chart">
      {/* baseline grid */}
      <line x1={PAD_L} y1={PAD_T + ih} x2={W - PAD_R} y2={PAD_T + ih} stroke="#2d2d2d" strokeWidth="1" />
      <line x1={PAD_L} y1={PAD_T}      x2={PAD_L}      y2={PAD_T + ih} stroke="#2d2d2d" strokeWidth="1" />
      {/* area under curve */}
      <path
        d={`${pathD} L${PAD_L + iw},${PAD_T + ih} L${PAD_L},${PAD_T + ih} Z`}
        fill="rgba(249,115,22,0.12)"
      />
      {/* line */}
      <path d={pathD} fill="none" stroke="#4d9c1f" strokeWidth="1.6" />
      {/* dots — clickable */}
      {dots.map((d) => (
        <circle
          key={d.m}
          cx={d.cx}
          cy={d.cy}
          r={Math.abs(d.m - activeMult) < 0.01 ? 4 : 2.5}
          fill={Math.abs(d.m - activeMult) < 0.01 ? "#fff" : "#4d9c1f"}
          stroke="#4d9c1f"
          strokeWidth="1"
          onClick={() => onPick?.(d.m)}
          className="cursor-pointer"
        >
          <title>{`${d.m}× · ${formatMt(d.t)}`}</title>
        </circle>
      ))}
      {/* elbow marker */}
      {elbow && (
        <>
          <line
            x1={elbow.cx} y1={PAD_T} x2={elbow.cx} y2={PAD_T + ih}
            stroke="#22c55e" strokeDasharray="2 3" strokeWidth="1"
          />
          <text x={elbow.cx + 4} y={PAD_T + 10} fill="#22c55e"
                fontSize="8" fontFamily="monospace">elbow · {elbow.m}×</text>
        </>
      )}
      {/* x-axis ticks */}
      {ticks.map((t) => (
        <text key={t.x} x={t.x} y={H - 3} fill="#666"
              fontSize="8" fontFamily="monospace" textAnchor="middle">{t.label}</text>
      ))}
      {/* y label */}
      <text x="4" y={PAD_T + 8} fill="#666" fontSize="8" fontFamily="monospace">tonnage</text>
    </svg>
  );
}

export default function SearchMultiplierSlider({
  projectId, value, onChange, onFilterMeta,
  compact = false, showChart = true, persistKey = "search_multiplier",
}) {
  const [rs, setRs] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sweepLoading, setSweepLoading] = useState(false);
  const [restored, setRestored] = useState(false);

  const fetchStats = (mult) => {
    setLoading(true);
    api.get(`/projects/${projectId}/resource-statement?search_multiplier=${mult}`)
      .then(({ data }) => {
        setRs(data);
        onFilterMeta?.(data.search_filter || {});
      })
      .catch(() => setRs(null))
      .finally(() => setLoading(false));
  };

  const fetchSweep = () => {
    setSweepLoading(true);
    api.get(`/projects/${projectId}/resource-statement/sweep`)
      .then(({ data }) => setSweep(data))
      .catch(() => setSweep(null))
      .finally(() => setSweepLoading(false));
  };

  const persist = (mult) => {
    // Fire-and-forget — a failed save must not block the UI.
    api.patch(`/projects/${projectId}`, {
      settings: { [persistKey]: mult },
    }).catch(() => {});
  };

  const commit = (mult) => {
    onChange(mult);
    fetchStats(mult);
    persist(mult);
  };

  // On mount, restore the previously-saved multiplier (if any) before
  // the first stats fetch so the UI opens on the user's last choice.
  useEffect(() => {
    if (!projectId || restored) return;
    api.get(`/projects/${projectId}`)
      .then(({ data }) => {
        const saved = data?.settings?.[persistKey];
        if (typeof saved === "number" && saved !== value) {
          onChange(saved);
          fetchStats(saved);
        } else {
          fetchStats(value);
        }
        if (showChart) fetchSweep();
        setRestored(true);
      })
      .catch(() => {
        fetchStats(value);
        if (showChart) fetchSweep();
        setRestored(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const sf = rs?.search_filter || {};

  return (
    <div className="border border-neutral-800 rounded-md bg-neutral-900/20 p-4"
         data-testid="search-multiplier-slider">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
            Drilled-envelope filter
          </div>
          <div className="text-[13px] text-neutral-100 mt-1">
            Restrict the resource to blocks within&nbsp;
            <code className="font-mono text-lime-300">
              {value.toFixed(1)}× mean sample spacing
            </code>
            &nbsp;of any drillhole collar.
          </div>
          {!compact && (
            <div className="text-[11.5px] text-neutral-500 mt-1 max-w-lg">
              Slide left for a conservative envelope (only well-drilled blocks);
              right to include exploration-quality extrapolation. The generated
              PDF uses the multiplier selected here.
            </div>
          )}
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-lime-500 shrink-0 mt-2" />}
      </div>

      <div className="flex items-center gap-3 mt-4">
        <span className="font-mono text-[10px] text-neutral-500 w-12 text-right">0.5×</span>
        <input
          type="range"
          min="0" max="4" step="0.25"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          onMouseUp={() => commit(value)}
          onTouchEnd={() => commit(value)}
          className="flex-1 accent-lime-600"
          data-testid="search-mult-slider"
        />
        <span className="font-mono text-[10px] text-neutral-500 w-8">4.0×</span>
        <button
          onClick={() => commit(0)}
          data-testid="search-mult-off"
          className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 border border-neutral-700 px-2 py-1 hover:border-lime-600 hover:text-lime-300 rounded-sm"
          title="Disable the filter — every block in the tenement (100 %)"
        >Off</button>
      </div>

      {rs?.search_filter && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 font-mono text-[11px]">
          <div>
            <div className="text-neutral-500 uppercase tracking-widest text-[9px]">Mean sample spacing</div>
            <div className="text-neutral-100 mt-0.5">{Number(sf.mean_sample_spacing_m).toLocaleString()} m</div>
          </div>
          <div>
            <div className="text-neutral-500 uppercase tracking-widest text-[9px]">Effective radius</div>
            <div className="text-neutral-100 mt-0.5">{Number(sf.effective_radius_m).toLocaleString()} m</div>
          </div>
          <div>
            <div className="text-neutral-500 uppercase tracking-widest text-[9px]">Collars used</div>
            <div className="text-neutral-100 mt-0.5">{sf.collar_count}</div>
          </div>
          <div>
            <div className="text-neutral-500 uppercase tracking-widest text-[9px]">Blocks kept</div>
            <div className="text-lime-300 mt-0.5" data-testid="search-mult-blocks-kept">
              {Number(sf.blocks_after_filter).toLocaleString()}
              &nbsp;/ {Number(sf.blocks_before_filter).toLocaleString()}
              &nbsp;({(Number(sf.kept_ratio) * 100).toFixed(1)}%)
            </div>
          </div>
        </div>
      )}

      {showChart && (
        <div className="mt-4 pt-4 border-t border-neutral-800/60">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">
              Resource tonnage vs multiplier
            </div>
            {sweepLoading && <Loader2 className="h-3 w-3 animate-spin text-lime-500" />}
          </div>
          {sweep?.points?.length > 0 ? (
            <>
              <SparklineChart
                points={sweep.points}
                activeMult={value}
                onPick={(m) => commit(m)}
              />
              <div className="text-[10px] text-neutral-500 mt-1 font-mono leading-tight">
                cutoff {(sweep.cutoff || 0).toFixed(3)} · click a dot to jump · the
                <span className="text-green-400">&nbsp;green line&nbsp;</span>
                marks the natural elbow (best conservative/inclusive balance)
              </div>
            </>
          ) : (
            <div className="text-[11px] text-neutral-500 italic">Loading sweep…</div>
          )}
        </div>
      )}
    </div>
  );
}
